import React, { useEffect, useState } from "react";
import {
  Banknote, Users, Calendar, Target,
  Wallet, Flame, Building2, UserCheck, Home, MapPinned,
} from "lucide-react";
import StatsCard            from "../components/dashboard/StatsCard";
import CallLogStatusCard   from "../components/dashboard/CallLogStatusCard";
import RecentLeads         from "../components/dashboard/RecentLeads";
import DealsOverview  from "../components/dashboard/DealsOverview";
import UpcomingVisits from "../components/dashboard/UpcomingVisits";
import CalendarView   from "../components/dashboard/CalendarView";
import "../css/dashboard/dashboard.css";
import api from "../api/api";

export default function Dashboard() {
  const [leads,      setLeads]      = useState([]);
  const [properties, setProperties] = useState([]);
  const [deals,      setDeals]      = useState([]);
  const [visits,     setVisits]     = useState([]);
  const [selfVisits, setSelfVisits] = useState([]);
  const [callLogs,   setCallLogs]   = useState([]);

  const [followups,        setFollowups]        = useState([]);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [hotLeads,         setHotLeads]         = useState([]);
  const [interestedLeads,  setInterestedLeads]  = useState([]);

  const userName = localStorage.getItem("name") || "there";

  useEffect(() => {
    fetchDashboardData();

    // ── Listen for deal stage updates and refresh dashboard ──
    const handleDealUpdate = (event) => {
      console.log("Deal stage updated, refreshing dashboard analytics...");
      fetchDashboardData();
    };

    window.addEventListener("dealStageUpdated", handleDealUpdate);
    return () => window.removeEventListener("dealStageUpdated", handleDealUpdate);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [leadsRes, propertiesRes, dealsRes, visitsRes, callLogsRes, selfVisitsRes] = await Promise.all([
        api.get("/leads/all"),
        api.get("/properties"),
        api.get("/deals"),
        api.get("/sitevisits"),
        api.get("/call-logs"),
        api.get("/self-visits"),
      ]);

      const leadsData = leadsRes.data || [];
      const logsData  = callLogsRes.data || [];

      setLeads(leadsData);
      setProperties(propertiesRes.data || []);
      setDeals(dealsRes.data || []);
      setVisits(Array.isArray(visitsRes.data?.data) ? visitsRes.data.data : []);
      setSelfVisits(Array.isArray(selfVisitsRes.data?.data) ? selfVisitsRes.data.data : []);
      setCallLogs(logsData);

      setFollowups(logsData.filter(l => l.currentStatus === "Follow-up"));
      setUpcomingMeetings(
        logsData.filter(l => l.meetingScheduled && new Date(l.meetingScheduled) >= new Date())
      );
      setHotLeads(leadsData.filter(l => l.interest_level === "hot"));
      setInterestedLeads(leadsData.filter(l => l.status === "interested"));
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    }
  };

  const stats = {
    totalLeads:            leads.length,
    newLeads:              leads.filter(l => l.status === "new").length,
    activeLeads:           leads.filter(l => !["closed", "lost"].includes(l.status)).length,
    activeDeals:           deals.filter(d => !["closed_won", "closed_lost"].includes(d.stage)).length,
    totalProperties:       properties.length,
    availableProperties:   properties.filter(p => p.status === "available").length,
    upcomingVisits:        visits.filter(v => v.status === "scheduled").length,
    upcomingSelfVisits:    selfVisits.filter(v => v.status === "scheduled").length,
    totalValue:            deals
                             .filter(d => d.stage === "closed_won")
                             .reduce((sum, d) => sum + Number(d.dealValue || 0), 0),
    conversionRate:        leads.length > 0
                             ? ((deals.filter(d => d.stage === "closed_won").length / leads.length) * 100).toFixed(1)
                             : 0,
    followupsCount:        followups.length,
    upcomingMeetingsCount: upcomingMeetings.length,
    hotAndInterestedCount: hotLeads.length + interestedLeads.length,
    hotLeadsCount:         hotLeads.length,
    interestedLeadsCount:  interestedLeads.length,
    totalCommission: deals
      .filter(d => d.stage === "closed_won")
      .reduce((s, d) => s + Number(d.commissionAmout || 0), 0),
    // Call log status counts
    callNew:               callLogs.filter(l => l.currentStatus === "New").length,
    callNotAnswering:      callLogs.filter(l => l.currentStatus === "Not Answering").length,
    callFollowUp:          callLogs.filter(l => l.currentStatus === "Follow-up").length,
    callInterested:        callLogs.filter(l => l.currentStatus === "Interested").length,
    callNotInterested:     callLogs.filter(l => l.currentStatus === "Not Interested").length,
    callClosed:            callLogs.filter(l => l.currentStatus === "Closed").length,
  };

  // Row 1 — Leads KPIs (4 cards)
  const row1Stats = [
    {
      title: "Total Leads", value: stats.totalLeads,
      subtitle: `${stats.newLeads} new this week`,
      icon: Users, color: "blue", to: "/leads",
    },
    {
      title: "Active Leads", value: stats.activeLeads,
      subtitle: "In pipeline",
      icon: UserCheck, color: "blue", to: "/leads",
    },
    {
      title: "Hot & Interested", value: stats.hotAndInterestedCount,
      subtitle: `${stats.hotLeadsCount} hot · ${stats.interestedLeadsCount} interested`,
      icon: Flame, color: "blue", to: "/leads",
    },
  ];

  // Row 2 — Property & visit KPIs (3 cards)
  const row2Stats = [
    {
      title: "All Properties", value: stats.totalProperties,
      subtitle: "Total listings",
      icon: Building2, color: "teal", to: "/properties",
    },
    {
      title: "Available Properties", value: stats.availableProperties,
      subtitle: "Ready to show",
      icon: Home, color: "teal", to: "/properties?status=available",
    },
    {
      title: "Upcoming Buyer Visits", value: stats.upcomingVisits,
      subtitle: "Scheduled visits",
      icon: Calendar, color: "amber", to: "/site-visits?status=scheduled",
    },
    {
      title: "Upcoming Seller Visits", value: stats.upcomingSelfVisits,
      subtitle: "Scheduled by users",
      icon: MapPinned, color: "amber", to: "/site-visits?tab=self&status=scheduled",
    },
  ];

  // Row 3 — Deal KPIs (3 cards)
  const row3Stats = [
    {
      title: "Conversion Ratio", value: `${stats.conversionRate}%`,
      subtitle: "Leads to closed deals",
      icon: Target, color: "emerald", to: "/deals?stage=closed_won",
    },
    {
      title: "Closed Deals Value", value: stats.totalValue,
      subtitle: "Won deals total",
      icon: Wallet, color: "emerald", formatCurrency: true, to: "/deals?stage=closed_won",
    },
    {
      title: "Commission Earned", value: stats.totalCommission,
      subtitle: "Total commission on won deals",
      icon: Banknote, color: "emerald", formatCurrency: true, to: "/deals?stage=closed_won",
    },
  ];


  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Good {getGreeting()}, {firstName(userName)}</h1>
        <p>Here's your real estate pipeline overview for today</p>
      </div>

      <div className="stats-grid first-row">
        {row1Stats.map((s, i) => <StatsCard key={i} {...s} />)}
      </div>

      <div className="stats-grid second-row">
        {row2Stats.map((s, i) => <StatsCard key={i} {...s} />)}
      </div>

      <div className="stats-grid third-row">
        {row3Stats.map((s, i) => <StatsCard key={i} {...s} />)}
      </div>

      <CallLogStatusCard stats={stats} />

      <div className="dashboard-content">
        <div className="left-panel">
          <RecentLeads leads={leads} />
        </div>
        <div className="right-panel">
          <CalendarView visits={visits} meetings={upcomingMeetings} />
          <UpcomingVisits visits={visits} />
        </div>
      </div>
    </div>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Morning";
  if (h < 17) return "Afternoon";
  return "Evening";
}

function firstName(name) {
  return name.split(" ")[0] || "there";
}
